// Theme variants for the dice-box physical substrate.
//
// Distinct axis from `dicePalettes` — palettes color the dice TINT via
// `themeColor` at roll() time, while themes swap the underlying TEXTURE
// (diffuse-light + bump + specular) that dice-box loads at init. The two
// compose: a 'gunmetal' theme under a 'bond' palette renders gold-tinted
// dark metal; under a 'casino' palette renders crimson-tinted dark metal.
//
// To add a theme:
//   1) author assets under
//        vibe-designer/frontend/public/assets/dice-box/themes/<systemName>/
//        (theme.config.json + diffuse-light.png + diffuse-dark.png +
//         normal.png + specular.jpg + default.json mesh — clone from
//         themes/default/ and edit the textures);
//   2) drop a new entry into DICE_THEMES below;
//   3) it auto-appears in the AtmosphereSettingsPanel selector;
//   4) no code change needed in DiceRollTumble — the systemName is what
//      dice-box loads from /assets/dice-box/themes/<systemName>/.
//
// Sprint 3 §4.2 (2026-06-06): first variant ships as 'gunmetal'. Per
// user choice 2026-06-06, theme authoring uses ImageMagick/PIL recolor
// of the default PNG textures (NOT scratch art generation) — keeps the
// mesh + numeral layout pixel-identical to the default theme, only
// swaps surface tint + sheen scalars.

export type DiceThemeId = 'default' | 'gunmetal' | 'ivory-lacquer' | 'translucent-jade' | 'bone';

export interface DiceTheme {
  id: DiceThemeId;
  /** systemName matches the `systemName` field inside the theme.config.json
   *  AND the directory name under /assets/dice-box/themes/. dice-box loads
   *  from `${assetPath}themes/${systemName}/`. */
  systemName: string;
  label: string;
  description: string;
}

export const DICE_THEMES: Record<DiceThemeId, DiceTheme> = {
  default: {
    id: 'default',
    systemName: 'default',
    label: 'Default — white plastic',
    description: 'Classic d10 plastic substrate; palette tints fully through.',
  },
  gunmetal: {
    id: 'gunmetal',
    systemName: 'gunmetal',
    label: 'Gunmetal — dark cool steel',
    description: 'Cool-gray metallic substrate; tints any palette toward dark metal.',
  },
  // Sprint 3.2 (2026-06-06): variant 2 of 4. Warm cream substrate + high-gloss
  // specular for the lacquered ivory feel — opposite axis from gunmetal (warm
  // light vs cool dark). theme.config.json sheen: bumpLevel 0.3 + specularPower 6.
  'ivory-lacquer': {
    id: 'ivory-lacquer',
    systemName: 'ivory-lacquer',
    label: 'Ivory Lacquer — warm cream gloss',
    description: 'Warm-cream lacquered substrate; reads as polished antique ivory.',
  },
  // Sprint 3.3 (2026-06-06): variant 3 of 4. Muted emerald substrate + very
  // high specular sheen for the "wet polished jade stone" feel. Lower
  // bumpLevel (0.25) keeps the surface smooth, specularPower 8 catches the
  // light sharply. Reads as semi-translucent gemstone die.
  'translucent-jade': {
    id: 'translucent-jade',
    systemName: 'translucent-jade',
    label: 'Translucent Jade — wet emerald gloss',
    description: 'Polished jade-stone substrate; semi-translucent emerald with sharp specular highlights.',
  },
  // Sprint 3.4 (2026-06-06): variant 4 of 4. Warm-gray bone substrate + MATTE
  // finish (specularPower 1) + higher bumpLevel (0.7) for the porous antique
  // bone texture. Distinct from ivory-lacquer (warm-cream glossy) on the same
  // warm axis — bone is matte and weathered where ivory is polished.
  bone: {
    id: 'bone',
    systemName: 'bone',
    label: 'Bone — weathered matte',
    description: 'Warm-cream matte substrate with porous surface; reads as antique bone.',
  },
};

export const DEFAULT_DICE_THEME: DiceThemeId = 'default';

/** Public: deterministic theme ordering for UI selectors. */
export const DICE_THEME_OPTIONS: DiceTheme[] = (Object.keys(DICE_THEMES) as DiceThemeId[]).map(
  (id) => DICE_THEMES[id],
);
